import { LitElement, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import pimsterGalleryWidgetConfig from './pimster-gallery-widget.config';

import {
  STORY_PREVIEW_DEFAULT_BORDER_COLOR,
  STORY_PREVIEW_DEFAULT_DISPLAY,
  STORY_PREVIEW_DEFAULT_ANIMATIONS,
  StoryPreviewDisplay,
  StoryPreviewAnimation,
  STORY_PREVIEW_DEFAULT_WITH_PLAY_ICON,
  STORY_PREVIEW_DEFAULT_WITH_RADIUS,
  STORY_PREVIEW_DEFAULT_WITH_TITLE,
} from '../pimster-story-preview/pimster-story-preview.constants';
import { trackImpression } from '../analytics/events/impression';

import { hasAutoplay } from '../pimster-story-preview/pimster-story-preview.utils';
import { CookieConsent, Widget } from '../enums';
import { InvalidArgumentError } from '../utils/errors';
import {
  GALLERY_DEFAULT_JUSTIFY,
  GalleryJustify,
  PATH_CONFIG_KEY,
} from './pimster-gallery-widget.constants';
import { pimsterGalleryWidgetStyle } from './pimster-gallery-widget.style';
import { EMBED_SERVER_URL } from '../constants';
import { renderContent } from './pimster-gallery-widget.render';
import { booleanConverter } from '../utils/converter';
import {
  InternalMessageType,
  isInternalMessageEvent,
  sendInternalMessage,
} from '../lib/internal-messages';

const { name } = pimsterGalleryWidgetConfig;

export type GalleryVisibilityState =
  | 'beforeEnter'
  | 'entering'
  | 'visible'
  | 'exiting'
  | 'out';

const GALLERY_BEFORE_ENTER_MARGIN = 500;

@customElement(name)
class PimsterGalleryWidget extends LitElement {
  // Properties
  @property({ type: Array<StoryPreviewAnimation> })
  animations = STORY_PREVIEW_DEFAULT_ANIMATIONS;

  @property({ type: String })
  border = undefined;

  @property({ type: String })
  borderColor = STORY_PREVIEW_DEFAULT_BORDER_COLOR;

  @property({ type: String })
  company = '';

  @property({ type: `${CookieConsent}` })
  cookieConsent = CookieConsent.Ask;

  @property({ type: `${StoryPreviewDisplay}` })
  display = STORY_PREVIEW_DEFAULT_DISPLAY;

  @property({ type: String })
  font = undefined;

  @property({ type: `${GalleryJustify}` })
  justify = GALLERY_DEFAULT_JUSTIFY;

  @property({ type: Number })
  moduleId = undefined;

  @property({ type: String })
  product = '';

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withPlayIcon = STORY_PREVIEW_DEFAULT_WITH_PLAY_ICON;

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withRadius = STORY_PREVIEW_DEFAULT_WITH_RADIUS;

  @property({
    type: Boolean,
    converter: booleanConverter,
  })
  withStoryTitle = STORY_PREVIEW_DEFAULT_WITH_TITLE;

  @property({ type: String })
  private iframeId: string = '';

  @query('iframe')
  iframe!: HTMLIFrameElement;

  @state()
  private computedHeight: string = '';

  private embedSrc: string = '';

  private _observers: IntersectionObserver[] = [];

  private _galleryVisibilityState: GalleryVisibilityState = 'out';

  static styles = pimsterGalleryWidgetStyle;

  private _buildRessourcesPath() {
    if (!this.company) {
      throw new InvalidArgumentError(
        'Element is missing at least one required argument'
      );
    }

    const path = `/company/${this.company}/product/${this.product || 'default'}/module/${this.moduleId}/gallery`;

    const config = {
      animations: this.animations,
      border: this.border,
      borderColor: this.borderColor,
      cookieConsent: this.cookieConsent,
      display: this.display,
      font: this.font,
      justify: this.justify,
      withPlayIcon: this.withPlayIcon,
      withRadius: this.withRadius,
      withStoryTitle: this.withStoryTitle,
    };

    const configParam = encodeURIComponent(JSON.stringify(config));

    return `${path}?${PATH_CONFIG_KEY}=${configParam}`;
  }

  // Lifecycle Methods
  async connectedCallback() {
    super.connectedCallback();
    this._attachMessageListener();
    this.setComputedHeight();
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
  }

  protected firstUpdated() {
    trackImpression({
      widgetType: Widget.Gallery,
      company: this.company,
      product: this.product,
      moduleId: `${this.moduleId}`,
      isAutoplay: hasAutoplay(this.animations),
    });
  }

  private _initializeGalleryVisibilityState() {
    const rect = this.iframe.getBoundingClientRect();
    const vh = window.innerHeight;
    const margin = GALLERY_BEFORE_ENTER_MARGIN;
    let initialState: GalleryVisibilityState = 'out';

    // Check if fully out
    if (rect.bottom <= 0 || rect.top >= vh + margin) {
      initialState = 'out';
    }
    // Check if in 200px margin but not intersecting viewport
    else if (
      rect.bottom > 0 &&
      rect.top < vh + margin &&
      (rect.bottom <= 0 || rect.top >= vh)
    ) {
      initialState = 'beforeEnter';
    }
    // Fully visible
    else if (rect.top >= 0 && rect.bottom <= vh) {
      initialState = 'visible';
    }
    // Partially visible
    else if (
      rect.top < vh &&
      rect.bottom > 0 &&
      !(rect.top >= 0 && rect.bottom <= vh)
    ) {
      initialState = 'entering';
    }

    if (initialState !== this._galleryVisibilityState) {
      this._galleryVisibilityState = initialState;
      this._sendGalleryVisibilityMessage(initialState);
    }
  }

  disconnectedCallback() {
    this._removeMessageListener();
    this._cleanupIntersectionObserver();
    super.disconnectedCallback();
  }

  protected update(changedProperties: PropertyValues): void {
    this.embedSrc = `${EMBED_SERVER_URL}${this._buildRessourcesPath()}`;
    this.setComputedHeight();
    super.update(changedProperties);
  }

  private setComputedHeight() {
    this.computedHeight = window.getComputedStyle(this).height;
  }

  private _setupIntersectionObserver() {
    // don't re-create
    if (this._observers.length > 0) return;
    // main 0/1 observer for entering, visible, exiting, out
    const transitionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          const ratio = entry.intersectionRatio;
          let newState: GalleryVisibilityState;

          if (ratio === 0 && !entry.isIntersecting) {
            // 0% visible → out
            newState = 'out';
          } else if (ratio === 1) {
            // 100% visible → visible
            newState = 'visible';
          } else if (entry.isIntersecting && ratio > 0 && ratio < 1) {
            // partially visible → entering or exiting?
            // if we were fully visible before, we must be exiting
            newState =
              this._galleryVisibilityState === 'visible'
                ? 'exiting'
                : 'entering';
          } else {
            // fallback to out
            newState = 'out';
          }

          if (newState !== this._galleryVisibilityState) {
            this._galleryVisibilityState = newState;
            this._sendGalleryVisibilityMessage(newState);
          }
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: [0, 1], // fire on crossing 0↔>0 and crossing <1↔1
      }
    );
    // // margin observer for beforeEnter (200px out)
    const marginObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          // only fire beforeEnter when we're currently fully "out"
          if (entry.isIntersecting && this._galleryVisibilityState === 'out') {
            this._galleryVisibilityState = 'beforeEnter';
            this._sendGalleryVisibilityMessage('beforeEnter');
          }
        }
      },
      {
        root: null,
        rootMargin: `${GALLERY_BEFORE_ENTER_MARGIN}px`,
        threshold: 0,
      }
    );

    // start observing the iframe
    if (this.iframe) {
      transitionObserver.observe(this.iframe);
      marginObserver.observe(this.iframe);
      this._observers.push(marginObserver);
      this._observers.push(transitionObserver);
    }
  }

  private _cleanupIntersectionObserver() {
    this._observers.forEach(observer => observer.disconnect());
    this._observers = [];
  }

  private _sendGalleryVisibilityMessage(state: GalleryVisibilityState) {
    sendInternalMessage({
      window: this.iframe.contentWindow,
      targetOrigin: '*',
      message: {
        type: InternalMessageType.GalleryVisibility,
        payload: { state },
      },
    });
  }

  private _messageHandler(message: MessageEvent) {
    if (isInternalMessageEvent(message)) {
      const { type, payload } = message.data;

      if (type === InternalMessageType.GalleryReady && payload.value === true) {
        this._initializeGalleryVisibilityState();
        this._setupIntersectionObserver();
      }
    }
  }

  private _attachMessageListener() {
    window.addEventListener('message', this._messageHandler.bind(this));
  }

  private _removeMessageListener() {
    window.removeEventListener('message', this._messageHandler.bind(this));
  }

  // Render Method
  render() {
    return renderContent({
      display: this.display,
      embedSrc: this.embedSrc,
      iframeId: this.iframeId,
      computedHeight: this.computedHeight,
    });
  }
}

export default PimsterGalleryWidget;
